import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Users,
  UserPlus,
  ClipboardList,
  Stethoscope,
  ArrowUpRight,
  Phone,
  Calendar,
  CalendarCheck,
  Sparkles,
  PillBottle,
  Receipt,
  AlertTriangle,
  CalendarOff,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardBody, CardHeader } from '@/components/common/Card'
import { Button } from '@/components/common/Button'
import { Badge } from '@/components/common/Badge'
import { EmptyState, Loader } from '@/components/common/Loader'
import { PatientAvatar } from '@/components/patient/PatientAvatar'
import { patientApi } from '@/api/patientApi'
import { drugApi } from '@/api/drugApi'
import { billingApi } from '@/api/billingApi'
import { useAuthStore } from '@/store/authStore'
import { formatDate, formatMobile, calculateAge, formatRupees } from '@/lib/utils'
import { CLINIC } from '@/lib/constants'
import { FEATURES } from '@/lib/features'

function StatCard({ icon: Icon, label, value, tone = 'primary', hint, to }) {
  const tones = {
    primary: 'from-primary-700 to-primary-900 text-cream',
    accent: 'from-accent-500 to-accent-700 text-cream',
    danger: 'from-rose-600 to-rose-800 text-cream',
    neutral: 'from-ink-800 to-ink-950 text-cream',
  }
  const Wrapper = to ? Link : 'div'
  return (
    <Wrapper
      to={to}
      className={`relative block overflow-hidden rounded-2xl bg-gradient-to-br shadow-card ring-1 ring-ink-100 transition ${tones[tone]} ${to ? 'hover:shadow-lift hover:-translate-y-0.5' : ''}`}
    >
      <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-cream/10 blur-2xl" />
      <div className="relative p-5">
        <div className="flex items-start justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cream/15 ring-1 ring-cream/25">
            <Icon className="h-5 w-5" />
          </div>
          {to && <ArrowUpRight className="h-4 w-4 text-cream/70" />}
        </div>
        <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.16em] text-cream/70">
          {label}
        </p>
        <p className="mt-1 h-display text-3xl font-bold">{value}</p>
        {hint && <p className="mt-1 text-xs text-cream/70">{hint}</p>}
      </div>
    </Wrapper>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [recent, setRecent] = useState([])
  const [allPatients, setAllPatients] = useState([]) // for accurate today/week counts
  const [total, setTotal] = useState(null)
  const [loading, setLoading] = useState(true)
  const [drugStats, setDrugStats] = useState({
    lowStock: null,
    nearExpiry: null,
    expired: null,
  })
  const [billStats, setBillStats] = useState({ count: null, outstanding: 0 })

  useEffect(() => {
    let alive = true
    setLoading(true)

    // Build the request list dynamically — we only call Phase 6/7 endpoints
    // when their features are enabled. This is what keeps the browser console
    // completely silent if the backend hasn't shipped those routes yet: we
    // simply don't make the requests at all.
    //
    // The endpoints we do call are also marked `silent: true` so that even a
    // momentary failure (CORS hiccup, backend restart) doesn't toast.
    const requests = [
      patientApi.list({ limit: 8, offset: 0 }, { silent: true }).catch(() => []),
      patientApi.list({ limit: 200, offset: 0 }, { silent: true }).catch(() => []),
      FEATURES.pharmacy
        ? drugApi.list({ filter: 'low_stock', limit: 200 }, { silent: true }).catch(() => [])
        : Promise.resolve(null),
      FEATURES.pharmacy
        ? drugApi.list({ filter: 'near_expiry', limit: 200 }, { silent: true }).catch(() => [])
        : Promise.resolve(null),
      FEATURES.pharmacy
        ? drugApi.list({ filter: 'expired', limit: 200 }, { silent: true }).catch(() => [])
        : Promise.resolve(null),
      FEATURES.billing
        ? billingApi.list({ status: 'active', limit: 200 }, { silent: true }).catch(() => [])
        : Promise.resolve(null),
    ]

    Promise.all(requests)
      .then(([r, all, low, near, exp, bills]) => {
        if (!alive) return
        setRecent(Array.isArray(r) ? r : [])
        const allArr = Array.isArray(all) ? all : []
        setAllPatients(allArr)
        setTotal(allArr.length || null)
        setDrugStats({
          lowStock: Array.isArray(low) ? low.length : 0,
          nearExpiry: Array.isArray(near) ? near.length : 0,
          expired: Array.isArray(exp) ? exp.length : 0,
        })
        const outstanding = (Array.isArray(bills) ? bills : []).reduce(
          (acc, b) => acc + Number(b.balance || 0),
          0,
        )
        setBillStats({
          count: Array.isArray(bills) ? bills.length : 0,
          outstanding,
        })
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  // Compute today + this-week registrations from the full patient list.
  // We use `allPatients` (up to 200 most recent) rather than just `recent`
  // (8 latest) so the counts are accurate even when registrations spike.
  const { todayCount, weekCount, weekTrend } = useMemo(() => {
    const now = new Date()
    const todayStr = now.toISOString().slice(0, 10)
    const sevenDaysAgo = new Date(now)
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6) // include today → 7 days
    const sevenDaysAgoStr = sevenDaysAgo.toISOString().slice(0, 10)

    const previousWeekStart = new Date(now)
    previousWeekStart.setDate(previousWeekStart.getDate() - 13)
    const previousWeekStartStr = previousWeekStart.toISOString().slice(0, 10)

    let today = 0
    let thisWeek = 0
    let lastWeek = 0
    for (const p of allPatients) {
      const reg = p?.registration_date
      if (!reg) continue
      if (reg === todayStr) today += 1
      if (reg >= sevenDaysAgoStr) thisWeek += 1
      else if (reg >= previousWeekStartStr) lastWeek += 1
    }

    // Trend hint comparing this 7-day window to the previous 7-day window.
    let trend = null
    if (lastWeek > 0) {
      const delta = thisWeek - lastWeek
      trend = delta === 0 ? 'Same as last week' : delta > 0 ? `+${delta} vs last week` : `${delta} vs last week`
    } else if (thisWeek > 0) {
      trend = 'New activity this week'
    }
    return { todayCount: today, weekCount: thisWeek, weekTrend: trend }
  }, [allPatients])

  const stockAlertCount =
    (drugStats.lowStock || 0) + (drugStats.nearExpiry || 0) + (drugStats.expired || 0)

  return (
    <div className="container-page py-6 sm:py-8 lg:py-10">
      <PageHeader
        eyebrow={`Hello, ${user?.name || 'Doctor'} 👋`}
        title="Today at the clinic"
        description={`A quick look at ${CLINIC.name}. Search patients, register a new case, or open a follow-up.`}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate('/patients')}
              rightIcon={<ArrowUpRight className="h-4 w-4" />}
            >
              All patients
            </Button>
            <Button
              onClick={() => navigate('/patients/new')}
              leftIcon={<UserPlus className="h-4 w-4" />}
            >
              New patient
            </Button>
          </>
        }
      />

      {/* Stats */}
      <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Users}
          label="Total patients"
          value={total ?? '—'}
          hint={`${todayCount} added today`}
          tone="primary"
          to="/patients"
        />
        {FEATURES.billing && (
          <StatCard
            icon={Receipt}
            label="Outstanding balance"
            value={formatRupees(billStats.outstanding, { decimals: 0 })}
            hint={`${billStats.count ?? '—'} active bills`}
            tone="accent"
            to="/billing"
          />
        )}
        {FEATURES.pharmacy && (
          <StatCard
            icon={PillBottle}
            label="Stock alerts"
            value={stockAlertCount}
            hint={
              stockAlertCount === 0
                ? 'Inventory is healthy'
                : `${drugStats.lowStock || 0} low · ${drugStats.nearExpiry || 0} near expiry · ${drugStats.expired || 0} expired`
            }
            tone={stockAlertCount > 0 ? 'danger' : 'neutral'}
            to="/drugs"
          />
        )}
        <StatCard
          icon={CalendarCheck}
          label="This week"
          value={loading ? '…' : weekCount}
          hint={weekTrend || `${weekCount === 1 ? 'patient registered' : 'patients registered'} in the last 7 days`}
          tone="neutral"
          to="/patients"
        />
      </div>

      {/* Quick actions */}
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/patients/new"
          className="flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-soft ring-1 ring-ink-100 transition hover:bg-cream/60 hover:shadow-card"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
            <UserPlus className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink-900">New patient</p>
            <p className="text-[11px] text-ink-500">Phase 1 → 5 walk-through</p>
          </div>
        </Link>
        {FEATURES.billing && (
          <Link
            to="/billing/new"
            className="flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-soft ring-1 ring-ink-100 transition hover:bg-cream/60 hover:shadow-card"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-50 text-accent-700">
              <Receipt className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900">New bill</p>
              <p className="text-[11px] text-ink-500">Service + medicines</p>
            </div>
          </Link>
        )}
        {FEATURES.pharmacy && (
          <Link
            to="/drugs/new"
            className="flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-soft ring-1 ring-ink-100 transition hover:bg-cream/60 hover:shadow-card"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
              <PillBottle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900">Add medicine</p>
              <p className="text-[11px] text-ink-500">Restock the inventory</p>
            </div>
          </Link>
        )}
        {FEATURES.pharmacy && (
          <Link
            to="/drugs?filter=expired"
            className="flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-soft ring-1 ring-ink-100 transition hover:bg-cream/60 hover:shadow-card"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-50 text-rose-700">
              <CalendarOff className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900">Expired stock</p>
              <p className="text-[11px] text-ink-500">{drugStats.expired ?? '—'} items</p>
            </div>
          </Link>
        )}
      </div>

      {/* Recent patients + side panels */}
      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            eyebrow="Recently registered"
            title="Latest patients"
            description="The 8 most recent records, newest first."
            action={
              <Link
                to="/patients"
                className="text-xs font-semibold text-primary-700 hover:text-primary-900"
              >
                View all →
              </Link>
            }
          />
          <CardBody className="!px-0 !pt-2">
            {loading ? (
              <Loader label="Loading recent patients…" />
            ) : recent.length === 0 ? (
              <EmptyState
                icon={<UserPlus className="h-6 w-6" />}
                title="No patients yet"
                description="Register your first patient and you'll see them here."
                action={
                  <Button onClick={() => navigate('/patients/new')}>
                    Add first patient
                  </Button>
                }
              />
            ) : (
              <ul className="divide-y divide-ink-100">
                {recent.slice(0, 8).map((p) => (
                  <li key={p.patient_id}>
                    <Link
                      to={`/patients/${p.patient_id}`}
                      className="flex items-center gap-3 px-6 py-3 transition hover:bg-primary-50/40"
                    >
                      <PatientAvatar name={p.name} id={p.patient_id} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink-900">
                          {p.name}
                        </p>
                        <p className="truncate text-xs text-ink-500">
                          {p.patient_id} · {p.age ?? calculateAge(p.dob)} yrs · {formatMobile(p.mobile_number)}
                        </p>
                      </div>
                      <Badge tone="muted" className="hidden sm:inline-flex">
                        {formatDate(p.registration_date || p.created_at)}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* Side panel */}
        <div className="space-y-6">
          {/* Stock alerts (only when pharmacy feature is on) */}
          {FEATURES.pharmacy && (
            <Card>
              <CardHeader
                eyebrow="Inventory"
                title="Stock alerts"
                description="Anything that needs your attention."
                action={
                  <Link
                    to="/drugs"
                    className="text-xs font-semibold text-primary-700 hover:text-primary-900"
                  >
                    Open →
                  </Link>
                }
              />
              <CardBody>
                <ul className="space-y-2 text-sm">
                  <AlertRow
                    icon={<AlertTriangle className="h-4 w-4 text-accent-700" />}
                    label="Low stock"
                    count={drugStats.lowStock}
                    to="/drugs?filter=low_stock"
                  />
                  <AlertRow
                    icon={<Calendar className="h-4 w-4 text-accent-700" />}
                    label="Near expiry"
                    count={drugStats.nearExpiry}
                    to="/drugs?filter=near_expiry"
                  />
                  <AlertRow
                    icon={<CalendarOff className="h-4 w-4 text-rose-700" />}
                    label="Expired"
                    count={drugStats.expired}
                    to="/drugs?filter=expired"
                  />
                </ul>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader
              eyebrow="Clinic"
              title={CLINIC.name}
              description={CLINIC.tagline}
            />
            <CardBody>
              <div className="space-y-2 text-sm text-ink-700">
                <p className="flex items-start gap-2">
                  <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-primary-700" />
                  <span>{CLINIC.address}</span>
                </p>
                <p className="flex items-start gap-2">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-primary-700" />
                  <span>{CLINIC.phone}</span>
                </p>
                <p className="flex items-start gap-2">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary-700" />
                  <span>GSTIN · {CLINIC.gstin}</span>
                </p>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  )
}

function AlertRow({ icon, label, count, to }) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-3 rounded-xl bg-cream/60 px-3 py-2 ring-1 ring-ink-100 transition hover:bg-cream"
      >
        {icon}
        <span className="flex-1 text-sm font-medium text-ink-800">{label}</span>
        <span className="tabular-nums font-bold text-ink-900">{count ?? '—'}</span>
        <ArrowUpRight className="h-3.5 w-3.5 text-ink-400" />
      </Link>
    </li>
  )
}
